/**
 * Data layer for `campaign-templates`. Thin typed fetch wrappers over /api/v1/campaign-templates.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type CampaignTemplateScope = "platform" | "tenant";

export type CampaignVertical =
	| "igaming"
	| "sports"
	| "casino"
	| "poker"
	| "lottery"
	| "esports"
	| "other";

export interface CampaignTemplateRow {
	id: string;
	tenant_id: string | null;
	template_code: string;
	name: string;
	description: string | null;
	vertical: CampaignVertical | string | null;
	scope: CampaignTemplateScope;
	is_active: boolean;
	created_at: number;
	updated_at: number;
	payload?: unknown;
}

export interface CreateCampaignTemplateBody {
	template_code: string;
	name: string;
	description?: string | null;
	vertical?: CampaignVertical | string | null;
	payload?: unknown;
}

export interface UpdateCampaignTemplateBody {
	name?: string;
	description?: string | null;
	vertical?: CampaignVertical | string | null;
	payload?: unknown;
	is_active?: boolean;
}

export interface CampaignTemplateDraft {
	template_code: string;
	name: string;
	vertical: string | null;
	payload: unknown;
	seeded_from_id: string;
	seeded_at: number;
}

const BASE = "/api/v1/campaign-templates";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(params?: {
	vertical?: string;
	includeInactive?: boolean;
}): Promise<CampaignTemplateRow[]> {
	const q = new URLSearchParams();
	if (params?.vertical) q.set("vertical", params.vertical);
	if (params?.includeInactive) q.set("include_inactive", "1");
	const qs = q.toString();
	const url = qs ? `${BASE}/list?${qs}` : `${BASE}/list`;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<CampaignTemplateRow[]>(res);
}

export async function getByCode(code: string): Promise<CampaignTemplateRow> {
	const res = await fetch(`${BASE}/${encodeURIComponent(code)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<CampaignTemplateRow>(res);
}

export async function create(
	body: CreateCampaignTemplateBody,
): Promise<CampaignTemplateRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CampaignTemplateRow>(res);
}

export async function update(
	id: string,
	body: UpdateCampaignTemplateBody,
): Promise<CampaignTemplateRow> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CampaignTemplateRow>(res);
}

export async function remove(id: string): Promise<{ ok: boolean; id: string }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: boolean; id: string }>(res);
}

export async function instantiate(code: string): Promise<CampaignTemplateDraft> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(code)}/instantiate`,
		{
			method: "POST",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: "{}",
		},
	);
	return parseOrThrow<CampaignTemplateDraft>(res);
}

export const campaignTemplatesQueryKeys = {
	all: ["campaign-templates"] as const,
	list: (params?: { vertical?: string; includeInactive?: boolean }) =>
		params
			? ([...["campaign-templates"], "list", params] as const)
			: (["campaign-templates", "list"] as const),
	detail: (id: string) => ["campaign-templates", id] as const,
};

// ---------------------------------------------------------------------------
// Compatibility aliases — TemplatesList.tsx was authored against an earlier
// shape. Re-export the same entities under the names it imports so the file
// type-checks and runs without changes. Do NOT remove; Wizard + TemplatesList
// still import these names directly.
// ---------------------------------------------------------------------------

export const CAMPAIGN_VERTICALS = [
	"igaming",
	"sports",
	"casino",
	"poker",
	"lottery",
	"esports",
	"other",
] as const satisfies readonly CampaignVertical[];

export type CampaignTemplateListItem = CampaignTemplateRow;
export type CreateCampaignTemplateInput = CreateCampaignTemplateBody;
export interface ListCampaignTemplatesOptions {
	vertical?: string;
	includeInactive?: boolean;
}

export const listCampaignTemplates = list;
export const createTenantCampaignTemplate = create;
